import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listClassrooms } from "../services/classroom";
import { getAttendanceRoster, saveAttendance, printAttendance } from "../services/studentAttendance";
import { getUserInfo } from "../services/auth";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const todayIso = () => new Date().toISOString().split("T")[0];

const PresencasEstudantesLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [filterYear, setFilterYear] = useState(currentYear);
  const [classrooms, setClassrooms] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [className, setClassName] = useState("");
  const [students, setStudents] = useState([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    setLoadingClassrooms(true);
    setClassId("");
    listClassrooms(filterYear)
      .then((resp) => setClassrooms(resp.data || []))
      .catch(() => toast.error(t("presenca.error.loadClassrooms")))
      .finally(() => setLoadingClassrooms(false));
  }, [filterYear, t]);

  useEffect(() => {
    if (!classId || !date) {
      setStudents([]);
      setClassName("");
      return;
    }
    setLoadingRoster(true);
    getAttendanceRoster({ classId, date })
      .then((resp) => {
        setClassName(resp.data?.className || "");
        setStudents(resp.data?.students || []);
      })
      .catch(() => toast.error(t("presenca.error.loadRoster")))
      .finally(() => setLoadingRoster(false));
  }, [classId, date, t]);

  const counts = useMemo(() => {
    return students.reduce(
      (acc, s) => {
        acc.total += 1;
        if (s.status === "ABSENT") acc.absent += 1;
        else if (s.status === "JUSTIFIED") acc.justified += 1;
        else acc.present += 1;
        return acc;
      },
      { present: 0, absent: 0, justified: 0, total: 0 },
    );
  }, [students]);

  const handleTogglePresent = (studentId) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.studentId === studentId
          ? s.status === "PRESENT"
            ? { ...s, status: "ABSENT", justification: "", justificationType: "" }
            : { ...s, status: "PRESENT", justification: "", justificationType: "" }
          : s,
      ),
    );
  };

  const handleToggleJustify = (studentId) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.studentId === studentId
          ? s.status === "JUSTIFIED"
            ? { ...s, status: "ABSENT", justification: "" }
            : { ...s, status: "JUSTIFIED" }
          : s,
      ),
    );
  };

  const handleJustificationChange = (studentId, value) => {
    setStudents((prev) => prev.map((s) => (s.studentId === studentId ? { ...s, justification: value } : s)));
  };

  const handleSave = async () => {
    if (!classId || !date) return;
    setSaving(true);
    try {
      const records = students.map((s) => ({
        studentId: s.studentId,
        studentName: s.studentName,
        status: s.status,
        justification: s.justification,
        justificationType: s.justificationType,
      }));
      const resp = await saveAttendance({
        classId,
        className,
        date,
        records,
        markedBy: getUserInfo()?.id,
      });
      setStudents(resp.data?.students?.map((s) => ({ ...s, recordId: s.id })) || students);
      toast.success(t("presenca.success.saved"));
    } catch (err) {
      toast.error(t("presenca.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!classId || !date) return;
    setPrinting(true);
    try {
      const resp = await printAttendance({ classId, date });
      navigate("/documentos/pdf", {
        state: {
          url: resp.data.pdf,
          title: t("presenca.documento.title", { className, date }),
          filename: `presencas-${className}-${date}.pdf`,
        },
      });
    } catch (err) {
      toast.error(t("presenca.error.print"));
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='row gy-3 align-items-end'>
              <div className='col-sm-3'>
                <label className='form-label'>{t("presenca.field.year")}</label>
                <select className='form-select' value={filterYear} onChange={(e) => setFilterYear(Number(e.target.value))}>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-5'>
                <label className='form-label'>{t("presenca.field.turma")}</label>
                <select
                  className='form-select'
                  value={classId}
                  disabled={loadingClassrooms}
                  onChange={(e) => setClassId(e.target.value)}
                >
                  <option value=''>{t("presenca.field.turma.placeholder")}</option>
                  {classrooms.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.className}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-4'>
                <label className='form-label'>{t("presenca.field.date")}</label>
                <div className="icon-field">
                  <span className="icon">
                    <Icon icon="solar:calendar-outline" />
                  </span>
                  <input
                    type='date'
                    className='form-control'
                    value={date}
                    max={todayIso()}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!classId && (
        <div className='col-12'>
          <div className='alert alert-info mb-0'>{t("presenca.hint.selectTurma")}</div>
        </div>
      )}

      {classId && (
        <>
          <div className='col-12'>
            <div className='row gy-3'>
              <div className='col-sm-3'>
                <div className='p-16 radius-8 bg-success-focus text-center'>
                  <span className='text-secondary-light text-sm d-block'>{t("presenca.stat.present")}</span>
                  <strong className='text-success-main text-xl'>{counts.present}</strong>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='p-16 radius-8 bg-danger-focus text-center'>
                  <span className='text-secondary-light text-sm d-block'>{t("presenca.stat.absent")}</span>
                  <strong className='text-danger-main text-xl'>{counts.absent}</strong>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='p-16 radius-8 bg-warning-focus text-center'>
                  <span className='text-secondary-light text-sm d-block'>{t("presenca.stat.justified")}</span>
                  <strong className='text-warning-main text-xl'>{counts.justified}</strong>
                </div>
              </div>
              <div className='col-sm-3'>
                <div className='p-16 radius-8 bg-primary-50 text-center'>
                  <span className='text-secondary-light text-sm d-block'>{t("presenca.stat.total")}</span>
                  <strong className='text-primary-600 text-xl'>{counts.total}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className='col-12'>
            <div className='card radius-12'>
              <div className='card-body'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16'>
                  <h6 className='mb-0'>{className}</h6>
                  <div className='d-flex gap-2'>
                    <button
                      type='button'
                      className='btn btn-outline-secondary-600 d-flex align-items-center gap-2'
                      onClick={handlePrint}
                      disabled={printing || students.length === 0}
                    >
                      <Icon icon='solar:printer-outline' />
                      {printing ? t("global.loading") : t("presenca.action.print")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 d-flex align-items-center gap-2'
                      onClick={handleSave}
                      disabled={saving || loadingRoster || students.length === 0}
                    >
                      <Icon icon='solar:diskette-outline' />
                      {saving ? t("global.loading") : t("presenca.action.save")}
                    </button>
                  </div>
                </div>

                {loadingRoster ? (
                  <div className='text-center py-40'>{t("global.loading")}</div>
                ) : students.length === 0 ? (
                  <div className='alert alert-warning mb-0'>{t("presenca.hint.noStudents")}</div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table bordered-table mb-0'>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>#</th>
                          <th>{t("presenca.col.name")}</th>
                          <th style={{ width: 160 }}>{t("presenca.col.status")}</th>
                          <th>{t("presenca.col.justification")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((s, idx) => (
                          <tr key={s.studentId}>
                            <td>{idx + 1}</td>
                            <td>{s.studentName}</td>
                            <td>
                              <div className='d-flex align-items-center gap-2'>
                                <div className='form-switch'>
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    role='switch'
                                    checked={s.status === "PRESENT"}
                                    onChange={() => handleTogglePresent(s.studentId)}
                                  />
                                </div>
                                <span
                                  className={
                                    s.status === "PRESENT"
                                      ? "text-success-main fw-medium"
                                      : s.status === "JUSTIFIED"
                                      ? "text-warning-main fw-medium"
                                      : "text-danger-main fw-medium"
                                  }
                                >
                                  {s.status === "PRESENT"
                                    ? t("presenca.status.present")
                                    : s.status === "JUSTIFIED"
                                    ? t("presenca.status.justified")
                                    : t("presenca.status.absent")}
                                </span>
                              </div>
                            </td>
                            <td>
                              {s.status !== "PRESENT" && (
                                <div className='d-flex align-items-center gap-2'>
                                  <div className='form-check mb-0'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={s.status === "JUSTIFIED"}
                                      onChange={() => handleToggleJustify(s.studentId)}
                                    />
                                  </div>
                                  {s.status === "JUSTIFIED" && (
                                    <div className="icon-field">
                                      <span className="icon">
                                        <Icon icon="solar:pen-2-outline" />
                                      </span>
                                      <input
                                        type='text'
                                        className='form-control form-control-sm'
                                        placeholder={t("presenca.field.justification.placeholder")}
                                        value={s.justification || ""}
                                        onChange={(e) => handleJustificationChange(s.studentId, e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PresencasEstudantesLayer;
